// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential

/** Which top-level screen the app shell shows before/around the router outlet. */
export type SplashScreen = 'auth-error' | 'spinner' | 'no-access' | 'app';

/** Raw boot signals that decide the splash screen. Booleans only — pure logic,
 *  no Angular/signal deps, so it's unit-testable in isolation (mirrors
 *  resolveNoAccessType in data.store). */
export interface SplashState {
  /** Whether OAuth claims have arrived (token parsed). */
  hasClaims: boolean;
  /** OAuth login still in flight. */
  loginProgress: boolean;
  /** A login attempt has settled (success or failure). */
  claimsLoaded: boolean;
  /** The current-user fetch is in flight. */
  userLoading: boolean;
  /** DataStore.isAuthorizedUser — true means the user fetch settled without a
   *  valid user (an error, or no data) → no access. */
  isAuthorizedUser: boolean;
}

/**
 * Decide the splash screen from the boot signals. Order is load-bearing:
 *  - `auth-error` is checked FIRST so a failed sign-in (claims settled but
 *    absent) can't be masked by the spinner — the bug behind the original
 *    infinite-splash lock.
 *  - `spinner` covers both an in-flight login (no claims yet) and the
 *    current-user fetch.
 *
 * Derives the two intermediate states inline so the whole screen-selection
 * logic is captured here rather than split across computeds:
 *  - authFailed  = claimsLoaded && !hasClaims  (settled with no claims → failed)
 *  - authBooting = loginProgress && !hasClaims (still signing in)
 */
export const resolveSplashScreen = (s: SplashState): SplashScreen => {
  const authFailed = s.claimsLoaded && !s.hasClaims;
  if (authFailed) return 'auth-error';

  const authBooting = s.loginProgress && !s.hasClaims;
  if (authBooting || s.userLoading) return 'spinner';

  if (s.isAuthorizedUser) return 'no-access';

  return 'app';
};
